import { request } from "./client";
import { ORGANISATION_BASE, authHeaders } from "./config";

export type UploadContext = "images" | "import" | "ai-emissions" | "evidence";

export interface UploadTicket {
  uploadUrl: string;
  s3Url: string;
  key: string;
  expiresIn: number;
  contentType: string;
  maxSizeBytes: number;
  method?: "POST" | "PUT";
  formFields?: Record<string, string>;
}

/**
 * Requests a short-lived, size-bounded upload ticket from the common file-upload endpoint
 * and uploads the file directly to S3. Returns the stable S3 object info.
 */
/** `20971520` -> `20MB` */
export function formatMaxSize(maxSizeBytes: number): string {
  const megabytes = maxSizeBytes / (1024 * 1024);
  return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)}MB`;
}

export function exceedsUploadLimit(file: File, maxSizeBytes: number): boolean {
  return file.size > maxSizeBytes;
}

export async function getUploadTicket(
  file: File,
  context: UploadContext,
  token?: string,
): Promise<UploadTicket> {
  const ticket = await request<UploadTicket>(
    "post",
    `${ORGANISATION_BASE}/upload-url`,
    {
      data: { fileName: file.name, context },
      ...(token ? authHeaders(token) : {}),
    },
  );
  // The ticket is the only authority on the limit: it is per-context and enforced
  // by the service, so a client-side constant can only ever disagree with it.
  if (exceedsUploadLimit(file, ticket.maxSizeBytes)) {
    throw new Error(
      `File exceeds the ${formatMaxSize(ticket.maxSizeBytes)} upload limit for ${context} files`,
    );
  }
  return ticket;
}

export async function putUpload(
  ticket: UploadTicket,
  file: File,
): Promise<void> {
  const form = ticket.method === "POST" ? new FormData() : undefined;
  if (form) {
    if (!ticket.formFields) throw new Error("Upload policy is missing");
    Object.entries(ticket.formFields).forEach(([key, value]) => form.append(key, value));
    form.append("file", file); // S3 requires the file field last.
  }
  const res = await fetch(ticket.uploadUrl, {
    method: form ? "POST" : "PUT",
    body: form ?? file,
    ...(form ? {} : { headers: { "Content-Type": ticket.contentType } }),
    signal: AbortSignal.timeout(120_000),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`S3 upload failed (${res.status}): ${body}`);
  }
}

/**
 * Deletes an uploaded object.
 *
 * The organisation service scopes the delete to the authenticated tenant and
 * refuses anything outside it, so this is safe to call with whatever URL the
 * caller is holding. Returns `{ deleted: true, key }` on success.
 */
export async function cleanupUploadedFile(s3Url: string): Promise<void> {
  await request("post", `${ORGANISATION_BASE}/upload-cleanup`, {
    data: { s3Url },
  });
}

/**
 * Legacy contract kept for existing callers: uploads a file and returns the
 * S3 object as `{ key, Location }` (key = object key, Location = object URL).
 */
export async function uploadFile(
  file: File,
  context: UploadContext = "images",
): Promise<{ key: string; Location: string }> {
  const ticket = await getUploadTicket(file, context);
  try { await putUpload(ticket, file); }
  catch (error) {
    // The S3 response can be lost after the object committed; the ticket still identifies it.
    try { await cleanupUploadedFile(ticket.s3Url); } catch { /* Preserve the upload error. */ }
    throw error;
  }
  return { key: ticket.key, Location: ticket.s3Url };
}
